import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
    ChevronRight,
    ChevronLeft,
    BatteryCharging,
    ShieldCheck,
    Cpu,
    ArrowUpRight,
    Play,
    Pause,
    Sparkles,
    UserCheck,
    HeartPulse,
    Maximize2,
    X,
    Layers,
    Award,
    Zap,
    SlidersHorizontal,
    Table,
    ChevronDown,
    ChevronUp,
    Check,
    Radio,
    Plus,
} from "lucide-react";
import SectionHeader from "../SectionHeader";
import {
    CuratedProduct,
    SHOWCASE_UI_TEXTS,
    getCuratedProducts,
} from "./curatedShowcaseData";

interface CuratedProductShowcaseProps {
    id?: string;
    productType?: "band" | "ring" | "watch";
    onCompareClick?: () => void;
    className?: string;
    titleRegular?: string;
    titleHighlighted?: string;
    descriptionText?: string;
}

export const CuratedProductShowcase: React.FC<CuratedProductShowcaseProps> = ({
    id = "curated-showcase",
    productType = "band",
    onCompareClick,
    className = "",
    titleRegular,
    titleHighlighted,
    descriptionText,
}) => {
    const { i18n } = useTranslation();
    const currentLang = i18n.language || "fa";
    const langKey = currentLang.startsWith("en")
        ? "en"
        : currentLang.startsWith("ar")
        ? "ar"
        : currentLang.startsWith("tr")
        ? "tr"
        : "fa";

    const isRTL = ["fa", "ar"].includes(langKey);
    const uiTexts = SHOWCASE_UI_TEXTS[langKey] || SHOWCASE_UI_TEXTS.fa;
    const defaultHeader = productType === "ring" ? uiTexts.ringHeader : uiTexts.bandHeader;

    const headerRegular = langKey === "fa" && titleRegular ? titleRegular : defaultHeader.titleRegular;
    const headerHighlighted = langKey === "fa" && titleHighlighted ? titleHighlighted : defaultHeader.titleHighlighted;
    const headerDescription = langKey === "fa" && descriptionText ? descriptionText : defaultHeader.description;

    const products = getCuratedProducts(productType, langKey);

    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState(0);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [progress, setProgress] = useState(0);
    const [activeTab, setActiveTab] = useState<"specs" | "marketing">("specs");

    // کنترل باز شدن مشخصات کامل فنی
    const [isFullSpecsExpanded, setIsFullSpecsExpanded] = useState(false);

    // میکرو اکشن‌های تصویر محصول
    const [showHotspots, setShowHotspots] = useState(true);
    const [activeHotspot, setActiveHotspot] = useState<number | null>(null);
    const [isZoomModalOpen, setIsZoomModalOpen] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const [isImageHovered, setIsImageHovered] = useState(false);

    const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const imageContainerRef = useRef<HTMLDivElement | null>(null);

    // Reset index if out of bounds on language/product change
    const safeIndex = currentIndex < products.length ? currentIndex : 0;
    const currentProduct = products[safeIndex] || products[0];

    // ۳ بج مشخصات و خدمات اختصاصی دلبند به صورت چندزبانه
    const delbandFeatureBadges = [
        { title: uiTexts.featureBadges.iranServices, icon: Radio },
        { title: uiTexts.featureBadges.installments, icon: Zap },
        { title: uiTexts.featureBadges.giftPackage, icon: Award },
    ];

    // محاسبه افکت زاویه دید سه‌بعدی نرم بر اساس حرکت ماوس
    const handleMouseMoveOnImage = (e: React.MouseEvent<HTMLDivElement>) => {
        if (!imageContainerRef.current) return;
        const rect = imageContainerRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width - 0.5;
        const y = (e.clientY - rect.top) / rect.height - 0.5;
        setMousePos({ x, y });
    };

    const handleMouseLeaveImage = () => {
        setIsImageHovered(false);
        setMousePos({ x: 0, y: 0 });
        setActiveHotspot(null);
    };

    // رفتن به اسلاید بعدی
    const handleNext = useCallback(() => {
        setDirection(1);
        setCurrentIndex((prev) => (prev + 1) % products.length);
        setProgress(0);
        setActiveHotspot(null);
        setIsFullSpecsExpanded(false);
    }, [products.length]);

    // رفتن به اسلاید قبلی
    const handlePrev = useCallback(() => {
        setDirection(-1);
        setCurrentIndex((prev) => (prev - 1 + products.length) % products.length);
        setProgress(0);
        setActiveHotspot(null);
        setIsFullSpecsExpanded(false);
    }, [products.length]);

    // انتخاب مستقیم اسلاید
    const handleSelectSlide = (index: number) => {
        if (index === currentIndex) return;
        setDirection(index > currentIndex ? 1 : -1);
        setCurrentIndex(index);
        setProgress(0);
        setActiveHotspot(null);
        setIsFullSpecsExpanded(false);
    };

    // تایمر خودکار تغییر اسلایدها
    useEffect(() => {
        if (!isAutoPlaying || isZoomModalOpen || isFullSpecsExpanded) {
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
            return;
        }

        const stepMs = 50;
        const totalDurationMs = 8500;
        const increment = (stepMs / totalDurationMs) * 100;

        progressIntervalRef.current = setInterval(() => {
            setProgress((prev) => {
                if (prev >= 100) {
                    handleNext();
                    return 0;
                }
                return prev + increment;
            });
        }, stepMs);

        return () => {
            if (progressIntervalRef.current) clearInterval(progressIntervalRef.current);
        };
    }, [isAutoPlaying, isZoomModalOpen, isFullSpecsExpanded, handleNext]);

    // کنترل کلیدهای جهت‌نما
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isZoomModalOpen && e.key === "Escape") {
                setIsZoomModalOpen(false);
                return;
            }
            if (e.key === "ArrowRight") {
                if (isRTL) handlePrev();
                else handleNext();
            } else if (e.key === "ArrowLeft") {
                if (isRTL) handleNext();
                else handlePrev();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isRTL, isZoomModalOpen, handleNext, handlePrev]);

    const slideVariants = {
        enter: (dir: number) => ({
            x: dir > 0 ? (isRTL ? -30 : 30) : (isRTL ? 30 : -30),
            opacity: 0,
            scale: 0.98,
        }),
        center: {
            x: 0,
            opacity: 1,
            scale: 1,
            transition: {
                x: { type: "spring" as const, stiffness: 320, damping: 32 },
                opacity: { duration: 0.35 },
                scale: { duration: 0.35 },
            },
        },
        exit: (dir: number) => ({
            x: dir > 0 ? (isRTL ? 30 : -30) : (isRTL ? -30 : 30),
            opacity: 0,
            scale: 0.98,
            transition: { duration: 0.25 },
        }),
    };

    if (!currentProduct) return null;

    return (
        <section
            id={id}
            data-section="curated-showcase"
            className={`my-16 md:my-24 relative overflow-hidden scroll-mt-24 ${className}`}
            dir={isRTL ? "rtl" : "ltr"}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
        >
            {/* سربرگ بخش با استایل تم لایت شفاف و پشتیبانی چندزبانه */}
            <SectionHeader
                regularText={headerRegular}
                highlightedText={headerHighlighted}
                descriptionText={headerDescription}
            />

            <div className="max-w-6xl mx-auto px-4">
                {/* ─── ۴ زبانه ناوبری بالا (Segmented Stepper) ─── */}
                <div className="mb-6 flex items-center justify-between gap-2 overflow-x-auto pb-2 scrollbar-none">
                    <div className="grid grid-cols-4 gap-2 sm:gap-3 w-full bg-slate-100/90 p-1.5 rounded-2xl backdrop-blur-md border border-slate-200/80 shadow-sm">
                        {products.map((item, idx) => {
                            const isActive = idx === safeIndex;
                            return (
                                <button
                                    key={item.id}
                                    onClick={() => handleSelectSlide(idx)}
                                    className={`relative py-2.5 px-2 sm:px-4 rounded-xl text-center transition-all duration-300 flex items-center justify-center gap-2 ${
                                        isActive
                                            ? "text-white font-bold shadow-md shadow-blue-500/20"
                                            : "text-slate-600 hover:text-slate-900 hover:bg-white/80 font-medium"
                                    }`}
                                >
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeSlideBackground"
                                            className="absolute inset-0 bg-gradient-to-r from-[#0038A8] to-[#0072CE] rounded-xl z-0"
                                            transition={{ type: "spring", stiffness: 400, damping: 35 }}
                                        />
                                    )}
                                    <span className="relative z-10 text-xs sm:text-sm tracking-wider font-mono opacity-80 hidden sm:inline-block">
                                        0{idx + 1}
                                    </span>
                                    <span className="relative z-10 text-xs sm:text-sm font-bold tracking-tight font-mono">
                                        {item.model}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* ─── صحنه اصلی اسلاید (تم تماماً لایت، غنی و خوش‌تراش) ─── */}
                <div className="relative rounded-3xl bg-white border border-slate-200/90 shadow-xl shadow-slate-200/50 overflow-hidden min-h-[580px]">
                    {/* نوار پیشرفت باریک اسلایدر در بالای کادر */}
                    <div className="absolute top-0 left-0 right-0 h-1 bg-slate-100 z-30">
                        <motion.div
                            className="h-full bg-gradient-to-r from-[#0038A8] to-[#0072CE]"
                            style={{ width: `${progress}%` }}
                            transition={{ ease: "linear" }}
                        />
                    </div>

                    <AnimatePresence initial={false} custom={direction} mode="wait">
                        <motion.div
                            key={`${currentProduct.id}-${langKey}`}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            className="p-6 sm:p-8 md:p-10 flex flex-col lg:grid lg:grid-cols-12 gap-6 lg:gap-8 items-start"
                        >
                            {/* ─── بخش ۱: اول تایتل و توضیح کوتاه در موبایل (در دسکتاپ بالای ستون اول) ─── */}
                            <div className="order-1 lg:col-span-7 w-full">
                                {/* عنوان دسته‌بندی */}
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="inline-block w-2 h-2 rounded-full bg-[#0072CE]" />
                                    <span className="text-xs sm:text-sm font-bold tracking-wide text-[#0072CE]">
                                        {currentProduct.category}
                                    </span>
                                </div>

                                {/* عنوان: فقط مدل با تایپوگرافی مدرن و مونو */}
                                <h3 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight mb-2 font-mono">
                                    {currentProduct.model}
                                </h3>

                                <p className="text-sm sm:text-base font-semibold text-slate-800 leading-snug mb-2">
                                    {currentProduct.tagline}
                                </p>

                                <p className="text-xs sm:text-sm text-slate-600 leading-relaxed max-w-xl">
                                    {currentProduct.description}
                                </p>
                            </div>

                            {/* ─── بخش ۲: عکس محصول و شاخص‌ها بعد از تایتل در موبایل (ستون دوم در دسکتاپ) ─── */}
                            <div className="order-2 lg:col-span-5 lg:row-span-2 w-full flex flex-col items-center justify-center gap-3">
                                {/* کادر استودیویی نمایش عکس با نور اختصاصی و کنترل‌های هاور */}
                                <div
                                    ref={imageContainerRef}
                                    onMouseMove={handleMouseMoveOnImage}
                                    onMouseEnter={() => setIsImageHovered(true)}
                                    onMouseLeave={handleMouseLeaveImage}
                                    className={`w-full max-w-[350px] aspect-square rounded-3xl bg-gradient-to-br ${currentProduct.bgGradient} p-6 flex items-center justify-center relative overflow-hidden border border-slate-200/90 shadow-sm group cursor-pointer`}
                                    onClick={() => setIsZoomModalOpen(true)}
                                >
                                    {/* هاله نور پس‌زمینه در تم لایت */}
                                    <motion.div
                                        className="absolute w-48 h-48 rounded-full blur-3xl opacity-40 pointer-events-none transition-all duration-300"
                                        style={{
                                            backgroundColor: currentProduct.accentColor,
                                            transform: `translate(${mousePos.x * 40}px, ${mousePos.y * 40}px)`,
                                        }}
                                    />

                                    {/* نوار ابزار بالای تصویر (دکمه‌های کنترلی هات‌اسپات و بزرگ‌نمایی) */}
                                    <div
                                        className="absolute top-3 left-3 right-3 z-30 flex items-center justify-end pointer-events-auto"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <div className="flex items-center gap-1.5">
                                            {currentProduct.hotspots && (
                                                <button
                                                    onClick={() => setShowHotspots((prev) => !prev)}
                                                    title={showHotspots ? uiTexts.hotspotToggle.hide : uiTexts.hotspotToggle.show}
                                                    className={`p-1.5 rounded-full border backdrop-blur-md transition-all shadow-sm ${
                                                        showHotspots
                                                            ? "bg-blue-600 text-white border-blue-600 shadow-blue-500/20"
                                                            : "bg-white/90 text-slate-600 border-slate-200 hover:bg-white"
                                                    }`}
                                                >
                                                    <Layers className="w-3.5 h-3.5" />
                                                </button>
                                            )}

                                            <button
                                                onClick={() => setIsZoomModalOpen(true)}
                                                title={uiTexts.zoomModalTitle}
                                                className="p-1.5 rounded-full bg-white/90 hover:bg-white text-slate-600 hover:text-slate-900 border border-slate-200 backdrop-blur-md transition-all shadow-sm hover:scale-105 active:scale-95"
                                            >
                                                <Maximize2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>

                                    {/* لایه تابش نور براق روی محصول حین هاور */}
                                    <div
                                        className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none -translate-x-full group-hover:translate-x-full transform ease-out"
                                        style={{ transitionDuration: "1000ms" }}
                                    />

                                    {/* تصویر محصول با انیمیشن سه‌بعدی Tilt، شناوری و بزرگ‌نمایی */}
                                    <motion.div
                                        className="relative z-10 flex items-center justify-center w-full h-full"
                                        animate={{
                                            rotateY: mousePos.x * 16,
                                            rotateX: -mousePos.y * 16,
                                            scale: isImageHovered ? 1.08 : 1,
                                            y: isImageHovered ? -4 : 0,
                                        }}
                                        transition={{ type: "spring", stiffness: 260, damping: 20 }}
                                    >
                                        <img
                                            src={currentProduct.image}
                                            alt={currentProduct.model}
                                            className="max-h-[250px] max-w-full object-contain drop-shadow-xl select-none transition-transform duration-300"
                                        />

                                        {/* هات‌اسپات‌های تعاملی حسگرها در تم لایت */}
                                        {showHotspots && currentProduct.hotspots && (
                                            <div className="absolute inset-0 pointer-events-none">
                                                {currentProduct.hotspots.map((spot, hIdx) => {
                                                    const isSelected = activeHotspot === hIdx;
                                                    const isTopHalf = spot.y < 50;
                                                    const positionClass = isTopHalf ? "top-full mt-2.5" : "bottom-full mb-2.5";
                                                    const horizontalClass =
                                                        spot.x > 70
                                                            ? (isRTL ? "left-0" : "right-0")
                                                            : spot.x < 30
                                                            ? (isRTL ? "right-0" : "left-0")
                                                            : "left-1/2 -translate-x-1/2";

                                                    return (
                                                        <div
                                                            key={hIdx}
                                                            style={{ left: `${spot.x}%`, top: `${spot.y}%` }}
                                                            className="absolute transform -translate-x-1/2 -translate-y-1/2 pointer-events-auto z-20"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveHotspot(isSelected ? null : hIdx);
                                                            }}
                                                            onMouseEnter={() => setActiveHotspot(hIdx)}
                                                        >
                                                            {/* پالس راداری */}
                                                            <div className="relative flex items-center justify-center">
                                                                <span className="animate-ping absolute inline-flex h-6 w-6 rounded-full bg-blue-400 opacity-60" />
                                                                <span className="relative flex items-center justify-center rounded-full h-5 w-5 bg-[#0038A8] text-white border-2 border-white shadow-md cursor-pointer hover:scale-125 transition-transform">
                                                                    <Plus className="w-2.5 h-2.5 stroke-[3] text-white" />
                                                                </span>
                                                            </div>

                                                            {/* تول‌تیپ اطلاعات سنسور در تم لایت */}
                                                            <AnimatePresence>
                                                                {isSelected && (
                                                                    <motion.div
                                                                        initial={{ opacity: 0, y: isTopHalf ? -6 : 6, scale: 0.9 }}
                                                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                                                        exit={{ opacity: 0, y: isTopHalf ? -4 : 4, scale: 0.95 }}
                                                                        transition={{ duration: 0.18 }}
                                                                        className={`absolute ${positionClass} w-48 max-w-[calc(100vw-80px)] p-2.5 rounded-xl bg-white/95 text-slate-800 backdrop-blur-md border border-slate-200 shadow-xl z-50 pointer-events-auto ${horizontalClass}`}
                                                                        onClick={(e) => e.stopPropagation()}
                                                                    >
                                                                        <div className="text-[11px] font-bold text-[#0038A8] flex items-center gap-1 mb-0.5">
                                                                            <Sparkles className="w-3 h-3 text-[#0072CE] shrink-0" />
                                                                            <span className="truncate">{spot.title}</span>
                                                                        </div>
                                                                        <div className="text-[10px] text-slate-600 leading-snug font-medium">
                                                                            {spot.desc}
                                                                        </div>
                                                                    </motion.div>
                                                                )}
                                                            </AnimatePresence>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </motion.div>

                                    {/* سایه کف استودیو */}
                                    <div className="absolute bottom-5 w-3/4 h-3.5 bg-slate-900/10 rounded-full blur-md z-0 pointer-events-none" />

                                    {/* بج راهنمای کلیک در پایین کادر */}
                                    <div className="absolute bottom-2.5 left-1/2 transform -translate-x-1/2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/95 border border-slate-200 backdrop-blur-md text-[10px] font-semibold text-slate-700 shadow-md whitespace-nowrap">
                                            <Maximize2 className="w-3 h-3 text-[#0072CE]" />
                                            <span>{uiTexts.zoomLabel}</span>
                                        </span>
                                    </div>
                                </div>

                                {/* مینی گرید ۴ شاخص فیزیکی و کلیدی زیر تصویر محصول در تم لایت */}
                                <div className="w-full max-w-[350px] grid grid-cols-2 gap-2">
                                    {currentProduct.quickStats.map((stat, sIdx) => (
                                        <div
                                            key={sIdx}
                                            className="p-2.5 rounded-xl bg-slate-50 border border-slate-200/80 text-center hover:bg-blue-50/60 hover:border-blue-200 transition-colors"
                                        >
                                            <div className="text-[10px] font-semibold text-slate-500">
                                                {stat.label}
                                            </div>
                                            <div className="text-xs font-bold text-slate-900 mt-0.5 font-mono">
                                                {stat.value}
                                            </div>
                                            <div className="text-[9px] text-slate-500 truncate mt-0.5">
                                                {stat.subtext}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* ─── بخش ۳: بعد از عکس در موبایل، باقی موارد (ایده‌آل برای، تب‌ها و دکمه) ─── */}
                            <div className="order-3 lg:col-span-7 w-full flex flex-col space-y-4">
                                {/* بخش کاربردی «ایده‌آل برای چه افرادی است؟» در تم لایت */}
                                <div className="p-3 rounded-2xl bg-blue-50/70 border border-blue-100/90 flex items-start gap-3">
                                    <div className="p-1.5 rounded-lg bg-blue-600 text-white shrink-0 mt-0.5 shadow-sm shadow-blue-500/20">
                                        <UserCheck className="w-4 h-4" />
                                    </div>
                                    <div>
                                        <span className="text-xs font-bold text-blue-950 block mb-0.5">
                                            {uiTexts.idealForTitle}
                                        </span>
                                        <p className="text-xs text-blue-900/85 font-medium leading-relaxed">
                                            {currentProduct.idealFor}
                                        </p>
                                    </div>
                                </div>

                                {/* سوییچر دوگانه محتوا (تم لایت): «مشخصات و سنسورها» در برابر «مزیت‌های کلیدی و ارزش خرید» */}
                                <div className="pt-1">
                                    <div className="flex items-center gap-2 mb-3 border-b border-slate-200/80 pb-2">
                                        <button
                                            onClick={() => setActiveTab("specs")}
                                            className={`text-xs sm:text-sm font-bold pb-1 px-1 transition-all relative flex items-center gap-1.5 ${
                                                activeTab === "specs"
                                                    ? "text-[#0038A8]"
                                                    : "text-slate-400 hover:text-slate-700"
                                            }`}
                                        >
                                            <SlidersHorizontal className="w-3.5 h-3.5" />
                                            <span>{uiTexts.tabs.specs}</span>
                                            {activeTab === "specs" && (
                                                <motion.div
                                                    layoutId="subTabIndicator"
                                                    className="absolute -bottom-[9px] left-0 right-0 h-0.5 bg-[#0038A8]"
                                                />
                                            )}
                                        </button>

                                        <span className="text-slate-300">|</span>

                                        <button
                                            onClick={() => setActiveTab("marketing")}
                                            className={`text-xs sm:text-sm font-bold pb-1 px-1 transition-all relative flex items-center gap-1.5 ${
                                                activeTab === "marketing"
                                                    ? "text-[#0038A8]"
                                                    : "text-slate-400 hover:text-slate-700"
                                            }`}
                                        >
                                            <Award className="w-3.5 h-3.5 text-amber-500" />
                                            <span>{uiTexts.tabs.marketing}</span>
                                            {activeTab === "marketing" && (
                                                <motion.div
                                                    layoutId="subTabIndicator"
                                                    className="absolute -bottom-[9px] left-0 right-0 h-0.5 bg-[#0038A8]"
                                                />
                                            )}
                                        </button>
                                    </div>

                                    {/* تب اول: شبکه مشخصات، سنسورها + قابلیت مشاهده مشخصات فنی کامل */}
                                    {activeTab === "specs" && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.25 }}
                                            className="space-y-3"
                                        >
                                            {/* گرید ۴ گانه مشخصات اصلی */}
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                                                <div className="p-3 rounded-xl bg-slate-50/90 border border-slate-200/80 flex items-start gap-2.5 hover:bg-slate-100/80 transition-colors">
                                                    <div className="p-1.5 rounded-lg bg-blue-100 text-[#0038A8] shrink-0 mt-0.5">
                                                        <BatteryCharging className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-bold text-slate-900">
                                                            {currentProduct.specs.battery.title}
                                                        </div>
                                                        <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                                                            {currentProduct.specs.battery.desc}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="p-3 rounded-xl bg-slate-50/90 border border-slate-200/80 flex items-start gap-2.5 hover:bg-slate-100/80 transition-colors">
                                                    <div className="p-1.5 rounded-lg bg-blue-100 text-[#0038A8] shrink-0 mt-0.5">
                                                        <HeartPulse className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-bold text-slate-900">
                                                            {currentProduct.specs.sensors.title}
                                                        </div>
                                                        <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                                                            {currentProduct.specs.sensors.desc}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="p-3 rounded-xl bg-slate-50/90 border border-slate-200/80 flex items-start gap-2.5 hover:bg-slate-100/80 transition-colors">
                                                    <div className="p-1.5 rounded-lg bg-blue-100 text-[#0038A8] shrink-0 mt-0.5">
                                                        <ShieldCheck className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-bold text-slate-900">
                                                            {currentProduct.specs.material.title}
                                                        </div>
                                                        <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                                                            {currentProduct.specs.material.desc}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="p-3 rounded-xl bg-slate-50/90 border border-slate-200/80 flex items-start gap-2.5 hover:bg-slate-100/80 transition-colors">
                                                    <div className="p-1.5 rounded-lg bg-blue-100 text-[#0038A8] shrink-0 mt-0.5">
                                                        <Cpu className="w-4 h-4" />
                                                    </div>
                                                    <div>
                                                        <div className="text-xs font-bold text-slate-900">
                                                            {currentProduct.specs.connectivity.title}
                                                        </div>
                                                        <div className="text-[11px] text-slate-600 mt-0.5 leading-snug">
                                                            {currentProduct.specs.connectivity.desc}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* کلید بازشونده برای مشاهده مشخصات کامل دقیق در همین بخش */}
                                            <div className="pt-1">
                                                <button
                                                    onClick={() => setIsFullSpecsExpanded((prev) => !prev)}
                                                    className="w-full py-2.5 px-3.5 rounded-xl bg-slate-100/80 hover:bg-slate-200/80 border border-slate-200 text-slate-700 font-bold text-xs flex items-center justify-between transition-all"
                                                >
                                                    <div className="flex items-center gap-2">
                                                        <Table className="w-4 h-4 text-[#0072CE]" />
                                                        <span>
                                                            {isFullSpecsExpanded
                                                                ? uiTexts.fullSpecs.close
                                                                : uiTexts.fullSpecs.open}
                                                        </span>
                                                    </div>
                                                    {isFullSpecsExpanded ? (
                                                        <ChevronUp className="w-4 h-4 text-slate-500" />
                                                    ) : (
                                                        <ChevronDown className="w-4 h-4 text-slate-500" />
                                                    )}
                                                </button>

                                                {/* بخش گسترش یافته مشخصات فنی کامل با انیمیشن روان و تم لایت */}
                                                <AnimatePresence>
                                                    {isFullSpecsExpanded && (
                                                        <motion.div
                                                            initial={{ opacity: 0, height: 0 }}
                                                            animate={{ opacity: 1, height: "auto" }}
                                                            exit={{ opacity: 0, height: 0 }}
                                                            transition={{ duration: 0.3 }}
                                                            className="overflow-hidden mt-2 p-3.5 rounded-2xl bg-white border border-slate-200/90 shadow-sm space-y-3"
                                                        >
                                                            {currentProduct.fullSpecsTable.map((group, gIdx) => (
                                                                <div key={gIdx} className="space-y-1.5">
                                                                    <div className="text-[11px] font-bold text-[#0038A8] border-b border-slate-100 pb-1 flex items-center gap-1.5">
                                                                        <span className="w-1.5 h-1.5 rounded-full bg-[#0072CE]" />
                                                                        <span>{group.title}</span>
                                                                    </div>
                                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                                        {group.items.map((it, itIdx) => (
                                                                            <div
                                                                                key={itIdx}
                                                                                className="p-2 rounded-lg bg-slate-50/80 border border-slate-100 text-[11px]"
                                                                            >
                                                                                <span className="text-slate-500 font-medium block">
                                                                                    {it.key}
                                                                                </span>
                                                                                <span className="text-slate-800 font-semibold mt-0.5 block">
                                                                                    {it.val}
                                                                                </span>
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </motion.div>
                                                    )}
                                                </AnimatePresence>
                                            </div>
                                        </motion.div>
                                    )}

                                    {/* تب دوم: کامپوننت ساختار یافته مزیت‌های کلیدی و پرزنت محصول در تم لایت */}
                                    {activeTab === "marketing" && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 6 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.25 }}
                                            className="p-4 rounded-2xl bg-slate-50/90 border border-slate-200/90 shadow-sm space-y-3.5"
                                        >
                                            {/* تیتر مزیت‌های کلیدی */}
                                            <div className="flex items-center gap-2">
                                                <span className="p-1 rounded-lg bg-amber-100 text-amber-700">
                                                    <Sparkles className="w-3.5 h-3.5" />
                                                </span>
                                                <span className="text-xs font-bold text-slate-900">
                                                    {currentProduct.marketPresentation.uspTitle}
                                                </span>
                                            </div>

                                            {/* ۳ کارت مزیت رقابتی ساختار یافته و تمیز */}
                                            <div className="space-y-2">
                                                {currentProduct.marketPresentation.uspItems.map((usp, i) => (
                                                    <div
                                                        key={i}
                                                        className="p-2.5 rounded-xl bg-white border border-slate-200/80 flex items-start gap-2.5 shadow-xs hover:border-blue-200 transition-colors"
                                                    >
                                                        <div className="p-1 rounded-full bg-emerald-100 text-emerald-700 shrink-0 mt-0.5">
                                                            <Check className="w-3.5 h-3.5" />
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center justify-between gap-2">
                                                                <span className="text-xs font-bold text-slate-800">
                                                                  {usp.title}
                                                                </span>
                                                                <span className="hidden sm:inline-flex text-[10px] font-bold text-[#0072CE] bg-blue-50 px-2 py-0.5 rounded-md shrink-0">
                                                                    {usp.tag}
                                                                </span>
                                                            </div>
                                                            <p className="text-[11px] text-slate-600 mt-1 leading-relaxed">
                                                                {usp.description}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* ۳ بج اختصاصی درخواستی کاربر: اتصال به سرویس های ایرانی، پرداخت قسطی، بسته بندی هدیه */}
                                            <div className="pt-2 border-t border-slate-200/70 grid grid-cols-1 sm:grid-cols-3 gap-2">
                                                {delbandFeatureBadges.map((badge, bIdx) => {
                                                    const IconComponent = badge.icon;
                                                    return (
                                                        <div
                                                            key={bIdx}
                                                            className="flex items-center gap-1.5 text-[11px] font-bold text-slate-800 bg-white p-2 rounded-xl border border-slate-200/80 shadow-xs"
                                                        >
                                                            <IconComponent className="w-3.5 h-3.5 text-[#0072CE] shrink-0" />
                                                            <span className="truncate">{badge.title}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </motion.div>
                                    )}
                                </div>

                                {/* دکمه اقدام اصلی (Call to action) */}
                                <div className="pt-2 flex items-center gap-3">
                                    {onCompareClick ? (
                                        <button
                                            onClick={onCompareClick}
                                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white font-bold text-sm shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
                                        >
                                            <span>{uiTexts.ctaCompare}</span>
                                            <ArrowUpRight className={`w-4 h-4 ${isRTL ? "rotate-[-90deg]" : ""}`} />
                                        </button>
                                    ) : (
                                        <a
                                            href="#compare-section"
                                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white font-bold text-sm shadow-md shadow-blue-500/20 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-[1.02] active:scale-[0.98] transition-all"
                                        >
                                            <span>{uiTexts.ctaDetails}</span>
                                            <ArrowUpRight className={`w-4 h-4 ${isRTL ? "rotate-[-90deg]" : ""}`} />
                                        </a>
                                    )}
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>

                    {/* ─── کنترل‌های اسلایدر (پایین کارت) ─── */}
                    <div className="bg-slate-50/95 border-t border-slate-200/80 px-6 py-3.5 flex items-center justify-between">
                        {/* شمارنده اسلاید */}
                        <div className="text-xs sm:text-sm font-bold text-slate-500 font-mono flex items-center gap-1.5">
                            <span className="text-slate-900">0{safeIndex + 1}</span>
                            <span className="text-slate-300">/</span>
                            <span>0{products.length}</span>
                            <span className="text-[11px] font-sans text-slate-500 font-bold mr-2 font-mono">
                                • {currentProduct.model}
                            </span>
                        </div>

                        {/* دکمه‌های ناوبری قبلی / بعدی و پخش خودکار */}
                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setIsAutoPlaying((prev) => !prev)}
                                aria-label={isAutoPlaying ? "Pause Auto-play" : "Play Auto-play"}
                                title={isAutoPlaying ? "Pause Auto-play" : "Play Auto-play"}
                                className="p-2 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-200/70 transition-colors"
                            >
                                {isAutoPlaying ? (
                                    <Pause className="w-3.5 h-3.5" />
                                ) : (
                                    <Play className="w-3.5 h-3.5" />
                                )}
                            </button>

                            <div className="w-[1px] h-4 bg-slate-300 mx-1" />

                            <button
                                onClick={isRTL ? handleNext : handlePrev}
                                aria-label="Previous Slide"
                                className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 transition-all shadow-xs active:scale-95"
                            >
                                {isRTL ? (
                                    <ChevronRight className="w-4 h-4" />
                                ) : (
                                    <ChevronLeft className="w-4 h-4" />
                                )}
                            </button>

                            <button
                                onClick={isRTL ? handlePrev : handleNext}
                                aria-label="Next Slide"
                                className="p-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 hover:text-slate-900 transition-all shadow-xs active:scale-95"
                            >
                                {isRTL ? (
                                    <ChevronLeft className="w-4 h-4" />
                                ) : (
                                    <ChevronRight className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ─── مدال تمام‌صفحه و بزرگ‌نمایی تصویر (تم لایت و شفاف) ─── */}
            <AnimatePresence>
                {isZoomModalOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setIsZoomModalOpen(false)}
                        className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-md flex items-center justify-center p-4"
                    >
                        <motion.div
                            initial={{ scale: 0.92, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.92, opacity: 0 }}
                            transition={{ type: "spring", stiffness: 350, damping: 30 }}
                            onClick={(e) => e.stopPropagation()}
                            className="relative max-w-xl w-full bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 flex flex-col items-center text-center shadow-2xl"
                        >
                            {/* دکمه بستن مدال */}
                            <button
                                onClick={() => setIsZoomModalOpen(false)}
                                className={`absolute top-4 ${isRTL ? "left-4" : "right-4"} p-2 rounded-full bg-slate-100 text-slate-500 hover:text-slate-900 hover:bg-slate-200 transition-colors`}
                            >
                                <X className="w-5 h-5" />
                            </button>

                            <div className="text-xs font-bold text-[#0072CE] uppercase tracking-wider mb-1">
                                {currentProduct.category}
                            </div>
                            <h4 className="text-2xl font-black text-slate-900 mb-2 font-mono">
                                {currentProduct.model}
                            </h4>

                            <div className="relative my-4 flex items-center justify-center w-full max-h-[340px]">
                                <div
                                    className="absolute w-56 h-56 rounded-full blur-3xl opacity-30"
                                    style={{ backgroundColor: currentProduct.accentColor }}
                                />
                                <img
                                    src={currentProduct.image}
                                    alt={currentProduct.model}
                                    className="max-h-[300px] max-w-full object-contain relative z-10 drop-shadow-xl"
                                />
                            </div>

                            <p className="text-xs text-slate-600 max-w-md mt-2 leading-relaxed font-medium">
                                {currentProduct.tagline}
                            </p>

                            <div className="mt-6 flex items-center gap-3">
                                {onCompareClick && (
                                    <button
                                        onClick={() => {
                                            setIsZoomModalOpen(false);
                                            onCompareClick();
                                        }}
                                        className="px-5 py-2.5 rounded-xl bg-[#0038A8] hover:bg-[#0072CE] text-white font-bold text-xs shadow-md shadow-blue-500/20 transition-all"
                                    >
                                        {uiTexts.zoomModalCompare}
                                    </button>
                                )}
                                <button
                                    onClick={() => setIsZoomModalOpen(false)}
                                    className="px-5 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium text-xs transition-all"
                                >
                                    {uiTexts.zoomModalClose}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default CuratedProductShowcase;
